import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from 'styles/components/HeaderNavigation.module.sass';
import zeroneLogo from 'assets/images/svg/logo-light.svg';
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg';
import menuIcon from 'assets/images/svg/menu-burger.svg';
import menuIconDark from 'assets/images/svg/menu-burger-dark.svg';
import closeIcon from '@iconify/icons-mdi/close';
import { Icon } from '@iconify/react';
import PrimaryButton from 'components/common/PrimaryButton';
import SecondaryButton from 'components/common/SecondaryButton';
import { useAuth } from 'hooks/AuthContext';
import ReusableIcon from 'components/common/ReusableIcon';
import { fetchCategories } from 'api/ReportApiService';
import { ReportCategory } from 'interface/ReportCategory';
import { resumeSubscription, syncSubscriptionFromStripe } from 'api/SubscriptionApiService';

interface HeaderNavigationProps {
  theme?: string;
}

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({ theme = 'light' }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState<ReportCategory[]>([]);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [reportsMobileDropdownOpen, setReportsMobileDropdownOpen] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [showVerifyBanner, setShowVerifyBanner] = useState(false);
  const [showCancellationBanner, setShowCancellationBanner] = useState(false);
  const [cancellationDismissed, setCancellationDismissed] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const navigate = useNavigate();
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const toggleQuickActions = () => setQuickActionsOpen(!quickActionsOpen);
  const { user, userSubscription, logout } = useAuth();

  useEffect(() => {
    const wouldNeedPayment =
      !!userSubscription?.paymentRequired &&
      userSubscription.name.toLocaleLowerCase() !== 'free';

    if (wouldNeedPayment) {
      // Deduplicate: only hit Stripe once per session. Legitimately overdue users
      // would otherwise trigger a Stripe API call on every auth refresh — at scale
      // that exhausts rate limits fast.
      const SYNC_KEY = 'subscription_sync_checked';
      const alreadyChecked = sessionStorage.getItem(SYNC_KEY) === 'true';

      if (alreadyChecked) {
        // Already verified this session — show the banner immediately.
        setPaymentRequired(true);
      } else {
        // First load with paymentRequired: true — check Stripe once.
        setPaymentRequired(false);
        void syncSubscriptionFromStripe({ tag: 'HeaderNavigation' }).then((result) => {
          sessionStorage.setItem(SYNC_KEY, 'true');
          if (result?.paymentStatus === 'paid') {
            // Stripe confirms active — refresh auth so the banner never shows.
            window.dispatchEvent(new Event('authChange'));
          } else {
            // Genuinely pending — show the banner.
            setPaymentRequired(true);
          }
        });
      }
    } else {
      // Clear the flag when the subscription is paid so a future pending state
      // triggers a fresh sync rather than skipping it.
      sessionStorage.removeItem('subscription_sync_checked');
      setPaymentRequired(false);
    }

    setShowVerifyBanner(!!user && user.emailVerified === false);
    setShowCancellationBanner(!!userSubscription?.scheduledCancellation && !cancellationDismissed);
  }, [user, userSubscription, cancellationDismissed]);

  useMemo(() => {
    const loadCategories = async () => {
      try {
        const fetchedCategories = await fetchCategories('HeaderNavigation');
        setCategories(fetchedCategories);
      } catch {
        setCategories([]);
      }
    }

    loadCategories();
  }, []);

  const handleLogIn = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/sign-up');
  };

  const handleMyProfile = () => {
    navigate('/profile');
  };

  const handleResumeSubscription = async () => {
    setIsResuming(true);
    try {
      const result = await resumeSubscription({ tag: 'HeaderNavigation.resume' });
      if (result.success) {
        setCancellationDismissed(true);
        window.dispatchEvent(new Event('authChange'));
      }
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <div className={styles['header-navigation']}>
      <div className={styles['header-navigation-container']}>
        {/* Desktop Header */}
        <div className={styles['desktop-header']}>
          <div className={styles['logo-wrapper']}>
            <Link to={'/'}>
              <img src={theme === 'light' ? zeroneLogo : zeroneLogoDark} alt="Zerone AnalytiQs Logo" />
            </Link>
          </div>
          <div className={styles['nav-and-actions']}>
            <nav className={styles['nav-links']} style={{ color: theme === 'light' ? '#fff' : '#005581' }}>
              <Link to="/pricing" className={styles['nav-link']}>Pricing & Solutions</Link>
              <Link to="/workspace" className={styles['nav-link']}>
                Workspace
              </Link>
              {/* Reports Dropdown */}
              <div
                className={styles['nav-dropdown-parent']}
                onMouseEnter={() => setReportsDropdownOpen(true)}
                onMouseLeave={() => setReportsDropdownOpen(false)}
                tabIndex={0}
              >
                <Link to="/report/categories" className={styles['nav-link']}>
                  Reports
                </Link>
                {reportsDropdownOpen && categories.length > 0 && (
                  <div className={styles['nav-dropdown-menu']}>
                    {categories.map(item => (
                      <Link
                        to={`/report/category/${item.id}`}
                        className={styles['nav-dropdown-link']}
                        key={item.id}
                        onClick={() => setReportsDropdownOpen(false)}
                      >
                        <span className={`${styles['dropdown-icon-wrapper']}`} style={{ backgroundColor: item.meta.secondaryColor}}>
                          <ReusableIcon
                            icon={item.meta.icon}
                            className={styles['dropdown-icon'] || 'dropdown-icon'}
                            color={item.meta.primaryColor ?? `#005581`}
                          />
                        </span>
                        <span className={styles['dropdown-label']}>{item.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/about-us" className={styles['nav-link']}>About Us</Link>
              <Link to="/blogs" className={styles['nav-link']}>Blog</Link>
              <Link to="/contact-us" className={styles['nav-link']}>Contact Us</Link>
            </nav>
            {/* Action Buttons */}
            {!user ? (
              <div className={styles['action-buttons']}>
                <SecondaryButton
                  text="Log in"
                  primaryColor="transparent"
                  textColor={theme === 'light' ? '#FFFFFF' : '#ff5522'}
                  hoverColor="#ff5522"
                  hoverTextColor="#FFFFFF"
                  doSomething={handleLogIn}
                />
                <PrimaryButton
                  text="Sign Up"
                  primaryColor="#FF5522"
                  textColor="#FFFFFF"
                  hoverColor="#FFFFFF"
                  hoverTextColor="#FF5522"
                  doSomething={handleSignUp}
                />
              </div>
            ) : (
              <div className={styles['user-action-menu']}>
                <div className={styles['user-avatar-wrapper']}>
                  <p style={{ color: theme === 'light' ? '#fff' : '#005581' }}>Hi {user.name}!</p>
                  <div className={styles['avatar']} onClick={toggleQuickActions} style={{ borderColor: theme === 'light' ? '#fff' : '#005581' }}>
                    {!user.profile || user.profile === '' ? (
                      <ReusableIcon
                        icon={'mynaui:user-circle-solid'}
                        className={styles['avatar-icon'] || 'avatar-icon'}
                        color={theme === 'light' ? '#fff' : '#005581'}
                      />
                    ) : (
                      <img src={user.profile} alt="User Avatar" className={styles['avatar-icon']} />
                    )}
                  </div>
                </div>
                <div className={styles['quick-actions-wrapper']} style={{
                  visibility: quickActionsOpen ? 'visible' : 'hidden',
                  opacity: quickActionsOpen ? 1 : 0,
                  transform: quickActionsOpen ? 'translateY(0)' : 'translateY(1)',
                }}>
                  <div className={styles['quick-action-item']}>
                    <p className={styles['item-text']} onClick={handleMyProfile}>My Profile</p>
                  </div>
                  <div className={styles['quick-action-item']}>
                    <p className={styles['item-text']} onClick={logout}>Log out</p>
                  </div>
                  <div className={styles['item-divider']} />
                  <div className={styles['quick-action-footer']}>
                    <p className={styles['footer-text']} style={{ textTransform: 'uppercase', fontWeight: '700' }}>{userSubscription?.name} plan</p>
                    <p className={styles['footer-text']}>
                      {userSubscription?.maxCredits === -1
                        ? 'Unlimited credits'
                        : user.credits === 0
                          ? 'No credits available'
                          : `${user.credits} credit${user.credits === 1 ? '' : 's'} available`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Mobile Header */}
        <div className={styles['mobile-header']}>
          <div className={styles['top-wrapper']}
            style={{
              borderImage: menuOpen
                ? 'linear-gradient(90deg, rgba(92, 150, 181, 0.1) 0%, #6FA8C5 50%, rgba(92, 150, 181, 0.1) 100%) 1'
                : 'none',
            }}>
            <Link to={'/'}>
              <img src={theme === 'light' ? zeroneLogo : zeroneLogoDark} alt="Zerone AnalytiQs Logo" />
            </Link>
            <button onClick={toggleMenu} className={styles['menu-toggle']}>
              {menuOpen ? (
                <Icon icon={closeIcon} style={{ color: theme === 'light' ? '#fff' : '#005581' }} />
              ) : (
                <img src={theme === 'light' ? menuIcon : menuIconDark} alt="menu" />
              )}
            </button>
          </div>
          {menuOpen && (
            <div className={styles['bottom-wrapper']}>
              <nav className={styles['mobile-nav-links']} style={{ color: theme === 'light' ? '#fff' : '#005581' }}>
                <Link to="/pricing" className={styles['mobile-link']} aria-label="Pricing Page">
                  Pricing & Solutions
                </Link>
                <Link to="/workspace" className={styles['mobile-link']} aria-label="Workspace Page">
                  Workspace
                </Link>
                {/* Reports Dropdown on Mobile */}
                <div className={styles['mobile-dropdown-parent']}>
                  <div
                    className={styles['mobile-link']}
                    role="button"
                    tabIndex={0}
                    onClick={() => setReportsMobileDropdownOpen(val => !val)}
                  >
                    Reports
                    <Icon
                      icon={reportsMobileDropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                      style={{ marginLeft: 8, fontSize: 18 }}
                    />
                  </div>
                  {reportsMobileDropdownOpen && categories && (
                    <div className={styles['mobile-dropdown-menu']}>
                      {categories.map(item => (
                        <Link
                          to={`/report/category/${item.id}`}
                          className={styles['mobile-dropdown-link']}
                          key={item.id}
                          onClick={() => setMenuOpen(false)}
                        >
                          <span className={`${styles['dropdown-icon-wrapper']}`} style={{ backgroundColor: item.meta.secondaryColor}}>
                            <ReusableIcon
                              icon={item.meta.icon}
                              className={styles['dropdown-icon'] || 'dropdown-icon'}
                              color={item.meta.primaryColor ?? `#005581`}
                            />
                          </span>
                          <span className={styles['dropdown-label']}>{item.title}</span>
                        </Link>
                      ))}

                    </div>
                  )}
                </div>
                <Link to="/about-us" className={styles['mobile-link']} aria-label="About Us Page">
                  About Us
                </Link>
                <Link to="/blogs" className={styles['mobile-link']} aria-label="Blog Page">
                  Blog
                </Link>
                <Link to="/contact-us" className={styles['mobile-link']}>
                  Contact Us
                </Link>
              </nav>
              {!user ? (
                <div className={styles['mobile-action-buttons']}>
                  <SecondaryButton
                    text="Log in"
                    primaryColor="transparent"
                    textColor={theme === 'light' ? '#FFFFFF' : '#ff5522'}
                    hoverColor="#ff5522"
                    hoverTextColor="#FFFFFF"
                    doSomething={handleLogIn}
                  />
                  <PrimaryButton
                    text="Sign Up"
                    primaryColor="#FF5522"
                    textColor="#FFFFFF"
                    hoverColor="#FFFFFF"
                    hoverTextColor="#FF5522"
                    doSomething={handleSignUp}
                  />
                </div>
              ) : (
                <div className={styles['mobile-action-buttons']}>
                  <PrimaryButton
                    text="My Profile"
                    primaryColor="#FF5522"
                    textColor="#FFFFFF"
                    hoverColor="#FFFFFF"
                    hoverTextColor="#FF5522"
                    doSomething={handleMyProfile}
                  />
                  <SecondaryButton
                    text="Logout"
                    width="100%"
                    primaryColor="#FFFFFF"
                    textColor="#707070"
                    hoverColor="#FFFFFF"
                    hoverTextColor="#FF5522"
                    doSomething={logout}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showVerifyBanner && !menuOpen && (
        <div className={styles['verify-email-status']}>
          <p className={styles['payment-status-text']}>
            Your email address is not verified. Please verify your email to unlock full access to your account.
          </p>
          <SecondaryButton
            text="Verify Email"
            primaryColor="#FF5522"
            textColor="#FFFFFF"
            hoverColor="#FFFFFF"
            hoverTextColor="#FF5522"
            doSomething={() => navigate('/verify-email')}
            width="auto"
          />
        </div>
      )}
      {paymentRequired && !menuOpen && (
        <div className={styles['payment-status']} style={{ display: paymentRequired ? 'flex' : 'none' }}>
          <p className={styles['payment-status-text']}>
            Action required on your subscription. Please update your payment method to ensure uninterrupted access to our services. For any inquiries or assistance, contact our{' '}
            <a className={styles['payment-link']} href="/contact-us">
              support team
            </a>
            .
          </p>
          <SecondaryButton
            text="Update Payment Method"
            primaryColor="#FF5522"
            textColor="#FFFFFF"
            hoverColor="#FFFFFF"
            hoverTextColor="#FF5522"
            doSomething={() => navigate('/subscription/payment')}
            width="auto"
          />
        </div>
      )}
      {showCancellationBanner && !menuOpen && (
        <div className={styles['cancellation-status']}>
          <p className={styles['cancellation-status-text']}>
            Your subscription is scheduled to cancel. Your access will end on{' '}
            <strong>{userSubscription?.nextBillingDate}</strong>. Resume now to keep uninterrupted access.
          </p>
          <div className={styles['cancellation-actions']}>
            <SecondaryButton
              text={isResuming ? 'Resuming…' : 'Resume Subscription'}
              primaryColor="#FF5522"
              textColor="#FFFFFF"
              hoverColor="#FFFFFF"
              hoverTextColor="#FF5522"
              doSomething={() => { void handleResumeSubscription(); }}
              width="auto"
            />
            <button
              className={styles['cancellation-dismiss']}
              onClick={() => setCancellationDismissed(true)}
              aria-label="Dismiss cancellation notice"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderNavigation;
